/**
 * StatsScene - Player Statistics Screen
 *
 * Features:
 * - Total stats: levels completed, enemies killed, soldiers collected, time played
 * - High scores per level in scrollable list
 * - Back navigation to LevelSelectScene
 * - Data pulled from SaveSystem
 *
 * Per PRD section 5.2 and Task #26 specifications
 */

import Phaser from 'phaser';
import {
  COLOR_CSS,
  COLORS,
  FONTS,
  TEXT_SIZES,
  GAME_WIDTH,
  GAME_HEIGHT,
  DURATIONS,
  SAFE_AREA,
  SPACING,
} from '../config';
import { Button } from '../ui/Button';
import { saveSystem } from '../systems/SaveSystem';
import { logSceneEvent } from '../utils/automation';
import { createCommandDeckBackground, createSceneHeader } from '../ui/SceneChrome';

export class StatsScene extends Phaser.Scene {
  private scrollContainer!: Phaser.GameObjects.Container;
  private scrollMask!: Phaser.GameObjects.Graphics;
  private scrollY: number = 0;
  private maxScroll: number = 0;
  private isDragging: boolean = false;
  private dragStartY: number = 0;
  private scrollStartY: number = 0;

  constructor() {
    super({ key: 'StatsScene' });
  }

  create(): void {
    logSceneEvent('StatsScene');
    this.cameras.main.fadeIn(DURATIONS.screenTransition, 0, 0, 0);
    createCommandDeckBackground(this, 0x00D4FF);

    // Reset scroll state
    this.scrollY = 0;
    this.isDragging = false;

    this.createHeader();
    this.createTotalStats();
    this.createHighScoresSection();
    this.createBackButton();
    this.setupScrollInput();
  }

  // ===========================================================================
  // CREATION
  // ===========================================================================

  /**
   * Creates the header title
   */
  private createHeader(): void {
    createSceneHeader(this, {
      title: 'Combat Intel',
      kicker: 'Lifetime command record',
      accent: 0x00D4FF,
      y: SAFE_AREA.top + 44,
    });
  }

  /**
   * Creates the total stats summary section
   */
  private createTotalStats(): void {
    const stats = saveSystem.getStats();
    const currentTier = saveSystem.getCurrentTier();

    const cardY = SAFE_AREA.top + 90;
    const cardWidth = GAME_WIDTH - SPACING.space5 * 2;
    const cardHeight = 180;
    const cardX = (GAME_WIDTH - cardWidth) / 2;

    // Card background
    const card = this.add.graphics();
    card.fillStyle(COLORS.bgOverlay, 0.6);
    card.fillRoundedRect(cardX, cardY, cardWidth, cardHeight, 12);

    // Stats rows
    const rowHeight = 36;
    const rowStartY = cardY + 24;
    const labelX = cardX + 20;
    const valueX = cardX + cardWidth - 20;

    const labelStyle = {
      fontFamily: FONTS.body,
      fontSize: `${TEXT_SIZES.body}px`,
      color: COLOR_CSS.uiTextSecondary,
    };

    const valueStyle = {
      fontFamily: FONTS.numbers,
      fontSize: `${TEXT_SIZES.body}px`,
      color: COLOR_CSS.gold,
    };

    // Current Tier
    this.add.text(labelX, rowStartY, 'Current Tier', labelStyle).setOrigin(0, 0.5);
    this.add.text(valueX, rowStartY, `Tier ${currentTier}`, valueStyle).setOrigin(1, 0.5);

    // Levels Completed
    this.add.text(labelX, rowStartY + rowHeight, 'Levels Completed', labelStyle).setOrigin(0, 0.5);
    this.add.text(valueX, rowStartY + rowHeight, this.formatNumber(stats.totalLevelsCompleted), valueStyle).setOrigin(1, 0.5);

    // Enemies Defeated
    this.add.text(labelX, rowStartY + rowHeight * 2, 'Enemies Defeated', labelStyle).setOrigin(0, 0.5);
    this.add.text(valueX, rowStartY + rowHeight * 2, this.formatNumber(stats.totalKills), valueStyle).setOrigin(1, 0.5);

    // Soldiers Collected
    this.add.text(labelX, rowStartY + rowHeight * 3, 'Soldiers Collected', labelStyle).setOrigin(0, 0.5);
    this.add.text(valueX, rowStartY + rowHeight * 3, this.formatNumber(stats.totalSoldiersCollected), valueStyle).setOrigin(1, 0.5);

    // Time Played
    this.add.text(labelX, rowStartY + rowHeight * 4, 'Time Played', labelStyle).setOrigin(0, 0.5);
    this.add.text(valueX, rowStartY + rowHeight * 4, this.formatTime(stats.totalPlayTimeSeconds), valueStyle).setOrigin(1, 0.5);
  }

  /**
   * Creates the high scores section with scrollable list
   */
  private createHighScoresSection(): void {
    const sectionY = SAFE_AREA.top + 290;
    const sectionHeight = GAME_HEIGHT - sectionY - SAFE_AREA.bottom - 80;

    // Section title
    this.add.text(
      GAME_WIDTH / 2,
      sectionY,
      'High Scores',
      {
        fontFamily: FONTS.display,
        fontSize: `${TEXT_SIZES.title}px`,
        color: COLOR_CSS.uiText,
        fontStyle: 'bold',
      }
    ).setOrigin(0.5);

    // Create scrollable container
    const listY = sectionY + 40;
    const listHeight = sectionHeight - 40;

    this.scrollContainer = this.add.container(0, listY);

    // Create mask for scroll area
    this.scrollMask = this.add.graphics();
    this.scrollMask.fillStyle(0xffffff);
    this.scrollMask.fillRect(0, listY, GAME_WIDTH, listHeight);

    const mask = new Phaser.Display.Masks.GeometryMask(this, this.scrollMask);
    this.scrollContainer.setMask(mask);

    // Populate high scores list
    const scores = saveSystem.getAllScores();
    const currentTier = saveSystem.getCurrentTier();
    const highestLevel = saveSystem.getHighestLevel();

    const rowHeight = 48;
    const cardWidth = GAME_WIDTH - SPACING.space5 * 2;
    const cardX = (GAME_WIDTH - cardWidth) / 2;
    let yOffset = 0;

    // Show scores for all tiers up to current
    for (let tier = 1; tier <= currentTier; tier++) {
      // Tier header (only show for tier 2+)
      if (currentTier > 1) {
        const tierHeader = this.add.text(
          GAME_WIDTH / 2,
          yOffset + 10,
          `— Tier ${tier} —`,
          {
            fontFamily: FONTS.display,
            fontSize: `${TEXT_SIZES.caption}px`,
            color: tier === currentTier ? COLOR_CSS.gold : COLOR_CSS.uiTextSecondary,
          }
        ).setOrigin(0.5);
        this.scrollContainer.add(tierHeader);
        yOffset += 32;
      }

      // Show all 10 levels for this tier
      for (let level = 1; level <= 10; level++) {
        const scoreKey = `${tier}-${level}`;
        const score = scores[scoreKey] || 0;
        const isUnlocked = tier < currentTier || (tier === currentTier && level <= highestLevel);
        const isCompleted = score > 0;

        // Row background
        const rowBg = this.add.graphics();
        rowBg.fillStyle(COLORS.bgOverlay, isCompleted ? 0.5 : 0.2);
        rowBg.fillRoundedRect(cardX, yOffset, cardWidth, rowHeight - 8, 8);
        this.scrollContainer.add(rowBg);

        // Level number
        const levelText = this.add.text(
          cardX + 16,
          yOffset + (rowHeight - 8) / 2,
          `Level ${level}`,
          {
            fontFamily: FONTS.body,
            fontSize: `${TEXT_SIZES.body}px`,
            color: isUnlocked ? COLOR_CSS.uiText : COLOR_CSS.uiTextSecondary,
          }
        ).setOrigin(0, 0.5);
        this.scrollContainer.add(levelText);

        // Score or status
        let scoreText: string;
        let scoreColor: string;

        if (isCompleted) {
          scoreText = this.formatNumber(score);
          scoreColor = COLOR_CSS.gold;
        } else if (isUnlocked) {
          scoreText = '—';
          scoreColor = COLOR_CSS.uiTextSecondary;
        } else {
          scoreText = '🔒';
          scoreColor = COLOR_CSS.uiTextSecondary;
        }

        const scoreDisplay = this.add.text(
          cardX + cardWidth - 16,
          yOffset + (rowHeight - 8) / 2,
          scoreText,
          {
            fontFamily: FONTS.numbers,
            fontSize: `${TEXT_SIZES.body}px`,
            color: scoreColor,
          }
        ).setOrigin(1, 0.5);
        this.scrollContainer.add(scoreDisplay);

        // Star indicator for completed levels
        if (isCompleted) {
          const star = this.add.text(
            cardX + cardWidth - 80,
            yOffset + (rowHeight - 8) / 2,
            '★',
            {
              fontFamily: FONTS.display,
              fontSize: `${TEXT_SIZES.body}px`,
              color: COLOR_CSS.gold,
            }
          ).setOrigin(0.5);
          this.scrollContainer.add(star);
        }

        yOffset += rowHeight;
      }

      // Add spacing between tiers
      if (tier < currentTier) {
        yOffset += 16;
      }
    }

    // Calculate max scroll
    const contentHeight = yOffset;
    this.maxScroll = Math.max(0, contentHeight - listHeight);
  }

  /**
   * Creates the back button
   */
  private createBackButton(): void {
    new Button(this, {
      x: GAME_WIDTH / 2,
      y: GAME_HEIGHT - SAFE_AREA.bottom - 50,
      text: 'Back',
      variant: 'secondary',
      width: 120,
      height: 44,
      onClick: () => this.goBack(),
    });
  }

  // ===========================================================================
  // SCROLLING
  // ===========================================================================

  /**
   * Sets up scroll input handling
   */
  private setupScrollInput(): void {
    const listY = SAFE_AREA.top + 330;
    const listHeight = GAME_HEIGHT - listY - SAFE_AREA.bottom - 80;

    // Create interactive zone for scrolling
    const scrollZone = this.add.rectangle(
      GAME_WIDTH / 2,
      listY + listHeight / 2,
      GAME_WIDTH,
      listHeight,
      0x000000,
      0
    ).setInteractive();

    scrollZone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      this.isDragging = true;
      this.dragStartY = pointer.y;
      this.scrollStartY = this.scrollY;
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (this.isDragging) {
        const deltaY = this.dragStartY - pointer.y;
        this.scrollY = Phaser.Math.Clamp(
          this.scrollStartY + deltaY,
          0,
          this.maxScroll
        );
        this.updateScrollPosition();
      }
    });

    this.input.on('pointerup', () => {
      this.isDragging = false;
    });

    // Mouse wheel support
    this.input.on('wheel', (_pointer: Phaser.Input.Pointer, _gameObjects: unknown[], _deltaX: number, deltaY: number) => {
      this.scrollY = Phaser.Math.Clamp(
        this.scrollY + deltaY * 0.5,
        0,
        this.maxScroll
      );
      this.updateScrollPosition();
    });
  }

  /**
   * Updates the scroll container position
   */
  private updateScrollPosition(): void {
    const listY = SAFE_AREA.top + 330;
    this.scrollContainer.y = listY - this.scrollY;
  }

  // ===========================================================================
  // NAVIGATION
  // ===========================================================================

  /**
   * Returns to level select
   */
  private goBack(): void {
    this.cameras.main.fadeOut(DURATIONS.screenTransition, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('LevelSelectScene');
    });
  }

  // ===========================================================================
  // UTILITIES
  // ===========================================================================

  /**
   * Formats a number with commas
   */
  private formatNumber(num: number): string {
    return num.toLocaleString();
  }

  /**
   * Formats seconds into readable time
   */
  private formatTime(seconds: number): string {
    if (seconds < 60) {
      return `${seconds}s`;
    }

    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) {
      const secs = seconds % 60;
      return secs > 0 ? `${minutes}m ${secs}s` : `${minutes}m`;
    }

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
}
